import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tag } from 'antd';
import ResponsiveTable from '../common/ResponsiveTable';
import type { ColumnsType } from 'antd/es/table';
import { SparkPlugItem } from '../../types/ProductItems';
import { getEditDeleteColumns } from '../../utils/columnsHelpers';
import ProductStatusDot from '../common/ProductStatusDot';

interface SparkPlugTableProps {
  data: SparkPlugItem[];
  onEdit: (item: SparkPlugItem) => void;
  onDelete: (key: string) => void;
  /** ids unhidden manually — pinned to the top, pale-yellow, prompting quantity entry */
  needsQtyIds?: string[];
  /** ids that should be pinned to the top (e.g. just scanned) */
  pinnedIds?: string[];
}

const SparkPlugTable: React.FC<SparkPlugTableProps> = ({ data, onEdit, onDelete, needsQtyIds = [], pinnedIds = [] }) => {
  const needsQtySet = useMemo(() => new Set(needsQtyIds.map(String)), [needsQtyIds]);
  const pinnedSet = useMemo(() => new Set([...needsQtyIds, ...pinnedIds].map(String)), [needsQtyIds, pinnedIds]);

  const columns: ColumnsType<SparkPlugItem> = [
    {
      title: 'Бренд',
      dataIndex: 'brand',
      key: 'brand',
      // Закреплена слева: при прокрутке вбок бренд остаётся на экране.
      // Без этого строка теряла опознавательный признак — было видно
      // «шестигранник · 16мм · шайба», а какой товар, непонятно.
      fixed: 'left' as const,
      sorter: (a, b) => a.brand.localeCompare(b.brand),
      width: 150,
      render: (brand: string, record) => (
        <span className="brand-cell">
          <ProductStatusDot item={record} />
          {brand}
        </span>
      ),
    },
    {
      title: 'Номер детали',
      dataIndex: 'partNumber',
      key: 'partNumber',
      sorter: (a, b) => a.partNumber.localeCompare(b.partNumber),
      width: 150
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      sorter: (a, b) => (a.sku ?? '').localeCompare(b.sku ?? ''),
      width: 90,
      render: (val: string) => val || '—',
    },
    {
      title: 'Модель',
      dataIndex: 'model',
      key: 'model',
      sorter: (a, b) => a.model.localeCompare(b.model),
      width: 170
    },
    {
      title: 'Тип электрода',
      dataIndex: 'electrodeType',
      key: 'electrodeType',
      sorter: (a, b) => a.electrodeType.localeCompare(b.electrodeType),
      width: 150,
    },
    {
      title: 'Кол-во электродов',
      dataIndex: 'electrodeCount',
      key: 'electrodeCount',
      sorter: (a, b) => a.electrodeCount - b.electrodeCount,
      width: 120,  align: 'left'
    },
    {
      title: 'Тип ключа',
      dataIndex: 'hexType',
      key: 'hexType',
      sorter: (a, b) => a.hexType.localeCompare(b.hexType),
      width: 130
    },
    {
      title: 'Размер ключа',
      dataIndex: 'hexSize',
      key: 'hexSize',
      sorter: (a, b) => a.hexSize.localeCompare(b.hexSize),
      width: 130
    },
    {
      title: 'Седло',
      dataIndex: 'seatType',
      key: 'seatType',
      sorter: (a, b) => a.seatType.localeCompare(b.seatType),
      width: 130
    },
    {
      title: 'Размер резьбы',
      dataIndex: 'threadSize',
      key: 'threadSize',
      sorter: (a, b) => a.threadSize.localeCompare(b.threadSize),
      width: 140
    },
    {
      title: 'Длина резьбы',
      dataIndex: 'threadLength',
      key: 'threadLength',
      sorter: (a, b) => a.threadLength.localeCompare(b.threadLength),
      width: 140
    },
    {
      title: 'Зазор',
      dataIndex: 'gap',
      key: 'gap',
      sorter: (a, b) => a.gap.localeCompare(b.gap),
      width: 100
    },
    {
      title: 'Калильное число',
      dataIndex: 'heatRating',
      key: 'heatRating',
      sorter: (a, b) => a.heatRating.localeCompare(b.heatRating),
      width: 140
    },
    {
      title: 'Количество',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity,
      width: 110,
      render: (qty: number, record) =>
        needsQtySet.has(String(record.id))
          ? <Tag color="gold">введите кол-во</Tag>
          : qty,
    },
    {
      title: 'Тип товара',
      dataIndex: 'original',
      key: 'original',
      filters: [
        { text: 'Оригинал', value: 'original' },
        { text: 'Копия', value: 'copy' },
      ],
      onFilter: (value, record) => record.original  === value,
      render: (val: string) => (val === 'original' ? 'Оригинал' : 'Копия'),
      sorter: (a, b) => a.original.localeCompare(b.original),
      width: 110
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => a.price - b.price,
      render: (text: number) => `${text} ֏`,
      width: 110
    },
    ...getEditDeleteColumns<SparkPlugItem>(onEdit, onDelete),
  ];

const [highlightId, setHighlightId] = useState<string | null>(() =>
  localStorage.getItem('highlightProductId')
);

// --- пагинация: контролируемый режим + запоминание выбора
const [pageSize, setPageSize] = useState<number>(() => {
  const saved = Number(localStorage.getItem('sparkplugs_pageSize'));
  return Number.isFinite(saved) && saved > 0 ? saved : 10;
});
const [current, setCurrent] = useState<number>(1);

// Pinned rows (just unhidden / scanned) float to the top by default.
const orderedData = useMemo(() => {
  if (pinnedSet.size === 0) return data;
  const pinned = data.filter((i) => pinnedSet.has(String(i.id)));
  const rest = data.filter((i) => !pinnedSet.has(String(i.id)));
  return [...pinned, ...rest];
}, [data, pinnedSet]);

// Когда данные загружаются — переходим на страницу с выделенным товаром
useEffect(() => {
  if (!highlightId || !data.length) return;
  const idx = data.findIndex((item) => item.id === highlightId);
  if (idx !== -1) setCurrent(Math.floor(idx / pageSize) + 1);
}, [highlightId, data.length, pageSize]);

// При появлении закреплённых строк — на первую страницу, чтобы они были видны
useEffect(() => {
  if (pinnedSet.size > 0) setCurrent(1);
}, [pinnedSet]);

// если после фильтрации/обновления данных текущая страница стала невалидной — вернёмся на 1
useEffect(() => {
  if ((current - 1) * pageSize >= data.length && data.length > 0) {
    setCurrent(1);
  }
}, [data.length, pageSize, current]);

return (
  <ResponsiveTable
    columns={columns}
    dataSource={orderedData}
    pagination={{
      current,
      pageSize,
      total: data.length,
      position: ['bottomCenter'],
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50, 100],
      onChange: (page, size) => {
        setCurrent(page);
        if (size !== pageSize) {
          setPageSize(size);
          localStorage.setItem('sparkplugs_pageSize', String(size));
        }
      },
      onShowSizeChange: (_page, size) => {
        setCurrent(1);
        setPageSize(size);
        localStorage.setItem('sparkplugs_pageSize', String(size));
      },
    }}
    rowKey="id"
    size="middle"
    scroll={{ x: 'max-content' }}
    rowClassName={(record) =>
      needsQtySet.has(String(record.id))
        ? 'needs-qty-row'
        : record.id === highlightId ? 'highlight-row' : ''
    }
    onRow={(record) => ({
      onDoubleClick: () => {
        if (highlightId === record.id) {
          localStorage.removeItem('highlightProductId');
          setHighlightId(null); // ← сразу убираем выделение без перезагрузки
        }
      },
    })}
  />
);
};

export default SparkPlugTable;
